import { useState } from 'react';
import { saveScoreCategories } from '../../services/businessService';
import type { RetellCallScore } from '../../types/callReport';

const CATEGORY_COLORS = ['#1D9E75', '#185FA5', '#BA7517', '#993556', '#533489'];

interface EditScoreCategoriesModalProps {
  teamId: string;
  scoreCategories: RetellCallScore[];
  onClose: () => void;
  onSuccess: () => void;
}

export function EditScoreCategoriesModal({ teamId, scoreCategories: initial, onClose, onSuccess }: EditScoreCategoriesModalProps) {
  const [categories, setCategories] = useState<RetellCallScore[]>(() => JSON.parse(JSON.stringify(initial)));
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState(false);

  const total = categories.reduce((s, c) => s + c.weight, 0);
  const totalOk = total === 100;

  const updateWeight = (index: number, weight: number) => {
    setCategories(prev => {
      const next = [...prev];
      next[index] = { ...next[index], weight };
      return next;
    });
  };

  const toggleCriteria = (catIdx: number, critIdx: number) => {
    setCategories(prev => {
      const next = [...prev];
      const cat = { ...next[catIdx] };
      const criteria = [...(cat.criteria || [])];
      const crit = { ...criteria[critIdx] };
      if (crit.disabled) {
        delete crit.disabled;
      } else {
        crit.disabled = true;
      }
      criteria[critIdx] = crit;
      cat.criteria = criteria;
      // Auto-zero weight if all criteria are disabled
      if (criteria.every(c => c.disabled)) {
        cat.weight = 0;
      }
      next[catIdx] = cat;
      return next;
    });
  };

  const toggleExpand = (index: number) => {
    setExpanded(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSave = async () => {
    if (!totalOk) return;
    setSaving(true);
    try {
      await saveScoreCategories(teamId, categories);
      onSuccess();
    } catch {
      // error handled by apiService
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Edit Score Categories</h2>

          {/* Category weights */}
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Category weights</div>

          <div className="border border-gray-200 rounded-xl p-5 bg-[#f9fafb] mb-6">
            <div className="flex justify-between items-center mb-5">
              <span className="text-[13px] font-semibold">Drag to redistribute</span>
              <span className={`text-[13px] font-semibold px-2.5 py-0.5 rounded-full ${totalOk ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                {total}% {totalOk ? '\u2713' : '\u2014 must equal 100%'}
              </span>
            </div>

            {/* Stacked bar */}
            <div className="h-2.5 rounded-md overflow-hidden flex gap-0.5 mb-5">
              {categories.map((cat, i) => (
                <div
                  key={i}
                  className="h-full rounded-sm transition-all"
                  style={{ width: `${cat.weight}%`, background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                />
              ))}
            </div>

            {/* Weight sliders */}
            {categories.map((cat, i) => {
              const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
              const allDisabled = !cat.highlighted && cat.criteria?.length > 0 && cat.criteria.every(c => c.disabled);
              return (
                <div key={i} className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 text-[13px]">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: allDisabled ? '#d1d5db' : color }} />
                      <span className={allDisabled ? 'text-gray-400' : ''}>{cat.title}</span>
                    </div>
                    <span className="text-sm font-semibold min-w-[36px] text-right" style={{ color: allDisabled ? '#9ca3af' : color }}>{cat.weight}%</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={cat.weight}
                    onChange={e => updateWeight(i, parseInt(e.target.value))}
                    disabled={allDisabled}
                    className={`w-full h-1 rounded appearance-none ${allDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    style={{
                      background: `linear-gradient(to right, ${color} 0%, ${color} ${cat.weight}%, #e5e7eb ${cat.weight}%, #e5e7eb 100%)`,
                      accentColor: color,
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Scoring criteria */}
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Scoring criteria</div>
          <p className="text-xs text-gray-500 mb-4 leading-snug">
            Toggle off any criteria that aren't relevant. Click a category to expand.
          </p>

          {categories.map((cat, ci) => {
            if (cat.highlighted) return null;
            if (!cat.criteria?.length) return null;

            const isOpen = expanded[ci];
            const activeCount = cat.criteria.filter(c => !c.disabled).length;
            const color = CATEGORY_COLORS[ci % CATEGORY_COLORS.length];

            return (
              <div key={ci} className="border border-gray-200 rounded-xl mb-3 overflow-hidden bg-white">
                <button
                  type="button"
                  onClick={() => toggleExpand(ci)}
                  className="flex items-center justify-between w-full px-4 py-3 bg-[#f9fafb] border-b border-gray-200 select-none cursor-pointer"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                    <span className="text-[13px] font-semibold">{cat.title}</span>
                    <span className="text-[11px] text-gray-400">{activeCount}/{cat.criteria.length} active</span>
                  </div>
                  <span className={`text-[10px] text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>{'\u25BC'}</span>
                </button>
                {isOpen && (
                  <div className="py-2">
                    {cat.criteria.map((crit, cri) => (
                      <div key={cri} className="flex items-center justify-between px-4 py-2 border-b border-gray-200 last:border-b-0">
                        <span className={`text-[13px] flex-1 ${crit.disabled ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                          {crit.text}
                        </span>
                        <label className="relative w-9 h-5 flex-shrink-0 cursor-pointer inline-block">
                          <input
                            type="checkbox"
                            checked={!crit.disabled}
                            onChange={() => toggleCriteria(ci, cri)}
                            className="opacity-0 w-0 h-0 absolute"
                          />
                          <span className={`absolute inset-0 rounded-full transition-colors ${!crit.disabled ? 'bg-[#1D9E75]' : 'bg-gray-300'}`} />
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${!crit.disabled ? 'translate-x-4' : ''}`} />
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!totalOk || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
